// Inline-flow image — sits next to text inside a paragraph or table cell,
// unlike the default `image` extension which is block-only. Intended for
// icons and small inline glyphs where the user wants
// "[img] some text" on the same line.
//
// Sizing model:
//   - `width` (number, px) — the rendered display width
//   - `naturalWidth`/`naturalHeight` (numbers) — the source dimensions; used
//     to set `aspect-ratio` so the rendered height stays proportional
//     WITHOUT the browser briefly reserving native pixel height before
//     CSS applies (the bug that made non-square PNGs blow up table-cell
//     height).
//
// Schema: `inline: true, group: 'inline', atom: true`. Uses the same
// `/api/files/...` storage URL as block images so toPublicAssetUrl resolves
// it correctly on the public renderer.
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    inlineImage: {
      insertInlineImage: (attrs: {
        src: string;
        alt?: string;
        width?: number | null;
        naturalWidth?: number | null;
        naturalHeight?: number | null;
        attachmentId?: string | null;
      }) => ReturnType;
      setInlineImageWidth: (width: number) => ReturnType;
    };
  }
}

interface InlineImageOptions {
  HTMLAttributes: Record<string, any>;
  view: any;
}

const DEFAULT_WIDTH = 32;

function clampWidth(w: unknown): number {
  const n = typeof w === 'number' ? w : parseFloat(String(w ?? ''));
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_WIDTH;
  // No artificial cap — the user picks the file, the file picks the size.
  // Lower bound 4px just to defend against accidental zero/NaN inputs.
  return Math.max(4, Math.round(n));
}

function parseDim(raw: string | null): number | null {
  if (!raw) return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export const InlineImage = Node.create<InlineImageOptions>({
  name: 'inlineImage',
  inline: true,
  group: 'inline',
  atom: true,
  draggable: true,
  selectable: true,

  addOptions() {
    return { HTMLAttributes: {}, view: null };
  },

  addAttributes() {
    return {
      src: {
        default: '',
        parseHTML: (el) => el.getAttribute('src'),
        renderHTML: (attrs) => (attrs.src ? { src: attrs.src } : {}),
      },
      alt: {
        default: '',
        parseHTML: (el) => el.getAttribute('alt') ?? '',
        renderHTML: (attrs) =>
          attrs.alt ? { alt: attrs.alt } : { alt: '' },
      },
      width: {
        default: DEFAULT_WIDTH,
        parseHTML: (el) => clampWidth(el.getAttribute('width')),
        renderHTML: (attrs) => {
          const w = clampWidth(attrs.width);
          const nw = parseDim(attrs.naturalWidth);
          const nh = parseDim(attrs.naturalHeight);
          // When we know the source dimensions, set `aspect-ratio` so the
          // browser reserves the right height immediately (no layout shift
          // from native pixel height). The img stays inline with text and
          // height scales proportionally from width — no `object-fit`
          // squashing required.
          const ar = nw && nh ? `${nw}/${nh}` : 'auto';
          // `line-height:0` on the img neutralises its own contribution to
          // the line-box descender so adjacent text doesn't get an extra
          // baseline gap below. `vertical-align:middle` centres it on the
          // text x-height.
          return {
            width: String(w),
            style: [
              `width:${w}px`,
              `height:auto`,
              `aspect-ratio:${ar}`,
              `vertical-align:middle`,
              `line-height:0`,
            ].join(';'),
          };
        },
      },
      naturalWidth: {
        default: null,
        parseHTML: (el) => parseDim(el.getAttribute('data-natural-width')),
        renderHTML: (attrs) =>
          attrs.naturalWidth
            ? { 'data-natural-width': String(attrs.naturalWidth) }
            : {},
      },
      naturalHeight: {
        default: null,
        parseHTML: (el) => parseDim(el.getAttribute('data-natural-height')),
        renderHTML: (attrs) =>
          attrs.naturalHeight
            ? { 'data-natural-height': String(attrs.naturalHeight) }
            : {},
      },
      attachmentId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-attachment-id'),
        renderHTML: (attrs) =>
          attrs.attachmentId
            ? { 'data-attachment-id': attrs.attachmentId }
            : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'img[data-type="inline-image"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'img',
      mergeAttributes({ 'data-type': 'inline-image' }, HTMLAttributes),
    ];
  },

  addNodeView() {
    if (!this.options.view) return null;
    return ReactNodeViewRenderer(this.options.view);
  },

  addCommands() {
    return {
      insertInlineImage:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              src: attrs.src,
              alt: attrs.alt ?? '',
              width: clampWidth(attrs.width ?? DEFAULT_WIDTH),
              naturalWidth: attrs.naturalWidth ?? null,
              naturalHeight: attrs.naturalHeight ?? null,
              attachmentId: attrs.attachmentId ?? null,
            },
          });
        },
      setInlineImageWidth:
        (width) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, {
            width: clampWidth(width),
          }),
    };
  },
});
